import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Heart, X, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { fetchUsage, claimCampaignAndCheckout } from "../lib/api";
import { track } from "../lib/analytics";
import {
  trackAddToCart,
  trackInitiateCheckout,
} from "../lib/marketingPixels";
import { safeLocal } from "../lib/safeStorage";

/**
 * PrideClaimBanner — in-app sticky strip offering Pride 2026 lock-in.
 *
 * Shows when ALL of these hold:
 *   • User is signed in
 *   • `usage.pride_2026_pending === true` (set at signup if visitor
 *     came from /pride and used the free-signup CTA)
 *   • `usage.pride_2026_pending_expires_at` is in the future
 *   • User hasn't already claimed founding_lock_in
 *   • Banner wasn't dismissed in the last 24h (the user can dismiss
 *     repeatedly; we resurface daily until the deadline so the
 *     reminder stays alive without being annoying)
 *   • Current route isn't a full-screen flow (camera, AR mirror,
 *     onboarding, admin, billing pages, etc.)
 *
 * On click → `claimCampaignAndCheckout({campaign: "pride_2026"})`
 * which mints a Stripe Checkout for the locked-in $49.99/yr.
 *
 * The countdown ticks once per minute (no per-second whiplash) and
 * recomputes from the absolute expiry timestamp so background tabs
 * stay accurate.
 */

const DISMISS_KEY = "trubeaute.prideClaimBanner.dismissedAt.v1";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24h — resurface daily

// Routes where the banner should NOT show — full-screen single-purpose
// flows + admin / billing / marketing surfaces where it would distract.
const SUPPRESSED = [
  /^\/$/,
  /^\/start/,
  /^\/pride/, // they're already on the Pride flow — banner redundant
  /^\/login/,
  /^\/auth\//,
  /^\/onboarding/,
  /^\/analyzing/,
  /^\/admin(\/|$)/,
  /^\/yc(\/|$)/,
  /^\/lock-in/,
  /^\/billing\//,
  /^\/pricing/,
  /^\/dev\//,
];

// Phase 10 (Feb 27 2026): countdown timers create artificial urgency
// and were flagged for removal. Replace the ticking "Xd Xh" string
// with a static, friendly deadline date so the offer message stays
// clear without ratcheting pressure on the user.
const formatDeadline = (expiresAtIso) => {
  if (!expiresAtIso) return null;
  const exp = new Date(expiresAtIso).getTime();
  if (!Number.isFinite(exp) || exp <= Date.now()) return null;
  try {
    return new Date(exp).toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
    });
  } catch {
    return null;
  }
};

const PrideClaimBanner = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());

  // Phase 10 (Feb 27 2026): the per-minute countdown ticker was
  // removed — Phase 10 explicitly bans countdown timers / artificial
  // urgency. We still keep a once-per-mount evaluation of
  // dismissedRecently via nowTick (set during handleDismiss), but no
  // longer tick the clock.

  // Fetch usage when the user is authed. Re-fetch whenever the
  // pathname changes — cheap call, keeps the banner in sync after
  // the user (e.g.) claims via /pride direct-buy in a separate tab.
  useEffect(() => {
    if (!user) {
      setUsage(null);
      return;
    }
    let cancelled = false;
    fetchUsage()
      .then((u) => {
        if (!cancelled) setUsage(u);
      })
      .catch(() => {
        if (!cancelled) setUsage(null);
      });
    return () => {
      cancelled = true;
    };
  }, [user, pathname]);

  // Has the user dismissed recently? Check localStorage TTL.
  // `nowTick` is bumped inside handleDismiss() so the next render
  // re-evaluates this memo against the freshly stored timestamp;
  // without that, the banner would still be visible until the next
  // unrelated re-render.
  const dismissedRecently = useMemo(() => {
    const at = safeLocal.getItem(DISMISS_KEY);
    if (!at) return false;
    const n = Number(at);
    if (!Number.isFinite(n)) return false;
    return Date.now() - n < DISMISS_TTL_MS;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nowTick is the manual re-evaluation trigger; the memo body intentionally reads safeLocal at call time.
  }, [nowTick]);

  const deadline = usage?.pride_2026_pending_expires_at
    ? formatDeadline(usage.pride_2026_pending_expires_at)
    : null;

  // Compose all the show conditions in one boolean. Easier to audit
  // than spreading them across the JSX with conditional rendering.
  const shouldShow =
    !!user &&
    !!usage?.pride_2026_pending &&
    !usage?.founding_lock_in &&
    usage?.tier !== "pro" && // already Pro? skip — they'd be downgrading
    !!deadline &&
    !dismissedRecently &&
    !SUPPRESSED.some((re) => re.test(pathname));

  if (!shouldShow) return null;

  const handleDismiss = () => {
    track("pride_2026_banner_dismissed");
    safeLocal.setItem(DISMISS_KEY, String(Date.now()));
    // Force a re-evaluation by ticking the clock — the next render
    // will see `dismissedRecently=true` and hide.
    setNowTick(Date.now());
  };

  const handleClaim = async () => {
    if (claiming) return;
    setClaiming(true);
    track("pride_2026_banner_claim_clicked");
    trackAddToCart({
      content_id: "pride_2026",
      content_name: "Pride 2026 · $49.99/yr forever",
      content_type: "subscription",
      value: 49.99,
      currency: "USD",
      quantity: 1,
    });
    try {
      const { url } = await claimCampaignAndCheckout({
        campaign: "pride_2026",
        origin_url: window.location.origin,
      });
      trackInitiateCheckout({
        content_id: "pride_2026",
        value: 49.99,
        currency: "USD",
      });
      window.location.href = url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Couldn't start Pride checkout. Try again.",
      );
      setClaiming(false);
    }
  };

  return (
    <div
      // Bottom-positioned strip so it doesn't fight the fixed Header
      // (z-50) for top real-estate. Stacking on mobile from bottom-up:
      //   • MobileTabBar         z-40, bottom-0  (≈72px tall)
      //   • Send Feedback pill   z-40, bottom-24 (≈40px tall, sits at ~96-136px)
      //   • PrideClaimBanner     z-30, bottom-36 (sits at ~144-198px)
      // → banner is above both, but tappable feedback pill takes
      // precedence on z-axis if any visual overlap. On desktop the
      // tab bar isn't rendered and the feedback pill is at bottom-6,
      // so we just float the banner near the bottom.
      className="fixed left-0 right-0 z-30 bottom-36 sm:bottom-4 px-3 sm:px-4 pointer-events-none"
      role="status"
      aria-live="polite"
      data-testid="pride-claim-banner"
    >
      <div
        className="pointer-events-auto max-w-3xl mx-auto rounded-2xl border border-[#9B6A6C]/30 shadow-[0_14px_38px_-12px_rgba(155,106,108,0.45)] backdrop-blur-md"
        style={{
          background:
            "linear-gradient(95deg, rgba(161,213,245,0.94), rgba(251,231,238,0.94) 50%, rgba(242,211,206,0.94))",
        }}
      >
        <div className="px-4 sm:px-5 py-2.5 sm:py-3 flex items-center gap-3">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Heart className="h-4 w-4 text-[#9B6A6C] fill-[#9B6A6C] shrink-0" />
            <span className="text-[13px] sm:text-sm text-[#2C2123] leading-snug truncate">
              <strong>Pride 2026 pricing</strong> locks in by{" "}
              <strong data-testid="pride-banner-deadline">{deadline}</strong>
              {" "}
              <span className="hidden sm:inline">
                · Claim $49.99/yr forever
              </span>
            </span>
          </div>
          <button
            type="button"
            onClick={handleClaim}
            disabled={claiming}
            className="shrink-0 inline-flex items-center gap-1.5 bg-[#9B6A6C] text-white hover:bg-[#805557] disabled:opacity-70 rounded-full px-3.5 sm:px-4 py-1.5 text-xs sm:text-sm font-medium transition-all whitespace-nowrap"
            data-testid="pride-banner-claim-btn"
          >
            {claiming ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span className="hidden sm:inline">Starting…</span>
              </>
            ) : (
              <>
                Claim
                <ArrowRight className="h-3.5 w-3.5" />
              </>
            )}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="shrink-0 inline-flex items-center justify-center h-7 w-7 rounded-full text-[#5C4B51] hover:bg-white/40 transition-colors"
            aria-label="Dismiss Pride banner"
            data-testid="pride-banner-dismiss-btn"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrideClaimBanner;
